// ─── Sprint 093 — Roamer Vault™ Service ──────────────────────────────────────
// The Vault is permanent intelligence. Items arrive here automatically once
// the Backpack processing pipeline completes.
//
// TODO: CLOUD INTEGRATION — when Supabase credentials are provided:
//   1. Replace load/save with GET /api/vault, POST /api/vault/items (API server)
//   2. API server uses SUPABASE_SERVICE_ROLE_KEY to read/write `vault_items` table
//   3. Add /api/vault/search endpoint backed by Supabase full-text / pg_vector search

import type { VaultItem, VaultCategory } from '@/types/roamerVault';
import { storageManager } from '@/services/storage/StorageManager';

const STORAGE_KEY = 'nomad:vault:v1';
export const VAULT_CHANGED_EVENT = 'nomad:vault-changed';

interface VaultStore {
  version: 1;
  items: VaultItem[];
}

function loadStore(): VaultStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { version: 1, items: [] };
    return JSON.parse(raw) as VaultStore;
  } catch {
    return { version: 1, items: [] };
  }
}

function saveStore(store: VaultStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch { /* quota exceeded — large file base64 may cause this */ }
  // Write-through to cloud (api-server → Supabase); localStorage stays the
  // synchronous cache/offline layer. Queues offline, never throws.
  storageManager.push('vault', 'store', store);
}

/**
 * Boot hydration: adopt the cloud copy only if strictly newer than the last
 * local write (gateway enforces this). Emits VAULT_CHANGED_EVENT so pages
 * re-read via the normal service functions.
 */
export async function hydrateVaultFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<VaultStore>('vault', 'store');
  if (!cloud || cloud.version !== 1 || !Array.isArray(cloud.items)) return false;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cloud)); } catch { return false; }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(VAULT_CHANGED_EVENT, { detail: { source: 'cloud' } }));
  }
  return true;
}

/** Return all vault items, newest first. */
export function getAllVaultItems(): VaultItem[] {
  const store = loadStore();
  return [...store.items].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/** Return items in a specific category. */
export function getVaultItemsByCategory(category: VaultCategory): VaultItem[] {
  return getAllVaultItems().filter(i => i.category === category);
}

/** Return a count map of category → item count. */
export function getVaultCounts(): Partial<Record<VaultCategory, number>> {
  const items = getAllVaultItems();
  const counts: Partial<Record<VaultCategory, number>> = {};
  for (const item of items) {
    counts[item.category] = (counts[item.category] ?? 0) + 1;
  }
  return counts;
}

/** Total number of items in the vault. */
export function getTotalVaultCount(): number {
  return loadStore().items.length;
}

/** Add a processed item to the vault. */
export function addVaultItem(item: VaultItem): void {
  const store = loadStore();
  store.items.push(item);
  saveStore(store);
}

/** Look up a single vault item by ID. */
export function getVaultItemById(id: string): VaultItem | null {
  return loadStore().items.find(i => i.id === id) ?? null;
}

/**
 * Full-text search across title, summary, tags, keywords, OCR text,
 * transcript, and content URL.
 *
 * TODO: CLOUD INTEGRATION — replace with Supabase full-text search
 *   (or pg_vector similarity search) via GET /api/vault/search?q=...
 */
export function searchVault(query: string): VaultItem[] {
  if (!query.trim()) return getAllVaultItems();
  const q = query.toLowerCase();
  return getAllVaultItems().filter(item => {
    return (
      item.title.toLowerCase().includes(q) ||
      (item.summary ?? '').toLowerCase().includes(q) ||
      item.tags.some(t => t.toLowerCase().includes(q)) ||
      item.keywords.some(k => k.toLowerCase().includes(q)) ||
      (item.ocrText ?? '').toLowerCase().includes(q) ||
      (item.transcript ?? '').toLowerCase().includes(q) ||
      (item.originalSource ?? '').toLowerCase().includes(q)
    );
  });
}

/** Delete a vault item by ID. */
export function deleteVaultItem(id: string): void {
  const store = loadStore();
  store.items = store.items.filter(i => i.id !== id);
  saveStore(store);
}

/** Clear all vault items (Factory Reset). */
export function clearVault(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  storageManager.remove('vault', 'store');
}
